import React, { useMemo } from 'react';
import { Button, Dropdown, Select, Tabs, Tag, Tooltip } from 'antd';
import { HistoryOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { PairListItem } from '../../../services/pairTradingApi';
import { formatAbbrev, formatInt } from '../../../utils/numberFormat';
import { NEW_PAIR_KEY, pairDisplayName, pairStatusLabel } from '../utils/pairSelection';

export interface PairSwitcherBarProps {
    /** null while the list is loading. */
    pairs: PairListItem[] | null;
    selectedKey: string | null;
    /** The client-only New pair tab is shown. */
    newTabOpen: boolean;
    /** Tab keys (dc ids or `new`) holding unsaved edits. */
    dirtyKeys: ReadonlySet<string>;
    /** Narrow screens: a dropdown instead of tabs. */
    compact?: boolean;
    onSelect: (key: string) => void;
    onNewPair: () => void;
    onCloseNewPair: () => void;
    /** Close the temporary read-only tab of a history (ENDED) pair. */
    onCloseHistory: () => void;
    /** The list failed to load; shown with a retry while there is nothing to show. */
    error?: string | null;
    onRetry?: () => void;
}

const STATUS_COLOR: Record<string, string> = {
    Draft: 'default',
    Active: 'blue',
    Running: 'green',
    Ending: 'gold',
    Ended: 'default',
};

const pnlColor = (n: number) => (n > 0 ? '#0ECB81' : n < 0 ? '#F6465D' : '#848E9C');

function PnlText({ value }: { value: string }) {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return (
        <span
            title={`Realized PnL ${formatInt(n, { signed: n !== 0 })} VND`}
            style={{ color: pnlColor(n), fontFamily: "'Courier New', monospace", fontSize: 11, fontWeight: 600 }}
        >
            {formatAbbrev(n, 2, { signed: n !== 0 })}
        </span>
    );
}

function TabLabel({ item, dirty }: { item: PairListItem; dirty: boolean }) {
    const status = pairStatusLabel(item);
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} data-testid={`pair-tab-${item.dataConfigId}`}>
            <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pairDisplayName(item)}
            </span>
            {dirty && <span title="Unsaved changes" style={{ color: '#F0B90B' }}>•</span>}
            <Tag color={STATUS_COLOR[status]} style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}>
                {status}
            </Tag>
            {item.lifecycleStatus !== 'DRAFT' && <PnlText value={item.realizedPnl} />}
        </span>
    );
}

const PairSwitcherBar: React.FC<PairSwitcherBarProps> = ({
    pairs,
    selectedKey,
    newTabOpen,
    dirtyKeys,
    compact = false,
    onSelect,
    onNewPair,
    onCloseNewPair,
    onCloseHistory,
    error,
    onRetry,
}) => {
    const openPairs = useMemo(
        () => (pairs ?? []).filter((p) => p.lifecycleStatus !== 'ENDED'),
        [pairs],
    );
    const history = useMemo(
        () => (pairs ?? [])
            .filter((p) => p.lifecycleStatus === 'ENDED')
            .sort((a, b) => Date.parse(b.endedAt ?? b.updatedAt) - Date.parse(a.endedAt ?? a.updatedAt)),
        [pairs],
    );
    // A history pair opened from the menu gets a temporary read-only tab.
    const tempHistory = history.find((p) => p.dataConfigId === selectedKey) ?? null;

    const newLabel = (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} data-testid="pair-tab-new">
            New pair
            {dirtyKeys.has(NEW_PAIR_KEY) && <span title="Unsaved changes" style={{ color: '#F0B90B' }}>•</span>}
            <Tag style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}>Unsaved</Tag>
        </span>
    );

    const historyMenu = (
        <Dropdown
            trigger={['click']}
            disabled={history.length === 0}
            menu={{
                items: history.map((p) => {
                    const pnl = Number(p.realizedPnl);
                    return {
                        key: p.dataConfigId,
                        label: (
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                                <span style={{ fontWeight: 600 }}>{pairDisplayName(p)}</span>
                                <span style={{ fontSize: 11, color: '#848E9C' }}>
                                    Ended {p.endedAt ? dayjs(p.endedAt).format('YYYY-MM-DD') : '—'}
                                    {p.endReason ? ` · ${p.endReason}` : ''}
                                    {' · '}
                                    <span style={{ color: pnlColor(pnl) }}>
                                        {formatInt(pnl, { signed: pnl !== 0 })} VND
                                    </span>
                                </span>
                            </div>
                        ),
                    };
                }),
                onClick: ({ key }) => onSelect(String(key)),
            }}
        >
            <Button size="small" icon={<HistoryOutlined />} data-testid="pair-history-button">
                History ({history.length})
            </Button>
        </Dropdown>
    );

    const newButton = (
        <Tooltip title="Start a new pair config with an empty form">
            <Button size="small" icon={<PlusOutlined />} onClick={onNewPair} data-testid="pair-new-button">
                New pair
            </Button>
        </Tooltip>
    );

    if (pairs === null && error) {
        return (
            <div style={{ padding: '4px 16px', fontSize: 12, color: '#F6465D' }} data-testid="pair-list-error">
                Could not load the pairs of this portfolio: {error}{' '}
                {onRetry && <Button size="small" type="link" onClick={onRetry}>Retry</Button>}
            </div>
        );
    }

    if (compact) {
        const options = [
            ...openPairs.map((p) => ({
                value: p.dataConfigId,
                label: `${pairDisplayName(p)}${dirtyKeys.has(p.dataConfigId) ? ' •' : ''} — ${pairStatusLabel(p)}`,
            })),
            ...(tempHistory
                ? [{ value: tempHistory.dataConfigId, label: `${pairDisplayName(tempHistory)} — Ended` }]
                : []),
            ...(newTabOpen
                ? [{ value: NEW_PAIR_KEY, label: `New pair${dirtyKeys.has(NEW_PAIR_KEY) ? ' •' : ''} — Unsaved` }]
                : []),
        ];
        return (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 8px', flexWrap: 'wrap' }}>
                <Select
                    size="small"
                    style={{ flex: 1, minWidth: 160 }}
                    value={selectedKey ?? undefined}
                    loading={pairs === null}
                    placeholder="Select a pair"
                    options={options}
                    onChange={(v) => onSelect(String(v))}
                    data-testid="pair-switcher-select"
                />
                {newButton}
                {historyMenu}
            </div>
        );
    }

    const items = [
        ...openPairs.map((p) => ({
            key: p.dataConfigId,
            label: <TabLabel item={p} dirty={dirtyKeys.has(p.dataConfigId)} />,
            closable: false,
        })),
        ...(tempHistory
            ? [{
                key: tempHistory.dataConfigId,
                label: <TabLabel item={tempHistory} dirty={false} />,
                closable: true,
            }]
            : []),
        ...(newTabOpen ? [{ key: NEW_PAIR_KEY, label: newLabel, closable: true }] : []),
    ];

    return (
        <div style={{ padding: '0 16px', flexShrink: 0 }} data-testid="pair-switcher-bar">
            <Tabs
                type="editable-card"
                size="small"
                hideAdd
                activeKey={selectedKey ?? undefined}
                onChange={(key) => onSelect(key)}
                onEdit={(key, action) => {
                    if (action !== 'remove') return;
                    if (key === NEW_PAIR_KEY) onCloseNewPair();
                    else onCloseHistory();
                }}
                items={items}
                tabBarStyle={{ margin: 0 }}
                tabBarExtraContent={{
                    right: (
                        <span style={{ display: 'inline-flex', gap: 6, marginLeft: 8 }}>
                            {newButton}
                            {historyMenu}
                        </span>
                    ),
                }}
            />
        </div>
    );
};

export default PairSwitcherBar;
